import type {AssistantPageContext, ContextEntity} from './api/types';

/**
 * Who publishes what. The layers are merged when a message is sent, so a page
 * and a view inside it (or a selection inside that) can each own their part
 * without overwriting each other:
 * - `route`: the provider itself, from the router (route pattern, URL, page title).
 * - `page`: the leaf page (`useAssistantPageContext`): breadcrumb, records, period, lens, filters.
 * - `view`: a view inside the page (`useAssistantViewContext`): overrides period/lens, adds filters.
 * - `selection`: what the user has picked within the page (`useAssistantSelection`).
 */
export type ContextLayer = 'route' | 'page' | 'view' | 'selection';

/** What one layer may contribute; every field is optional and merged by `mergeContextLayers`. */
export type ContextLayerValue = Partial<
    Pick<
        AssistantPageContext,
        'path' | 'title' | 'url' | 'entities' | 'period' | 'lens' | 'filters' | 'selection'
    >
>;

export type ContextLayers = Partial<Record<ContextLayer, ContextLayerValue>>;

const entityKey = (entity: ContextEntity) => `${entity.kind}|${entity.id}`;

/**
 * Merges the layers into the context the server expects. Pure, so it is unit
 * tested on its own. Returns null when nothing has been published.
 */
export function mergeContextLayers(
    layers: ContextLayers,
    capturedAt: Date = new Date()
): AssistantPageContext | null {
    const {route, page, view, selection} = layers;
    if (!route && !page && !view && !selection) {
        return null;
    }

    const seen = new Set<string>();
    const entities: ContextEntity[] = [];
    for (const entity of [...(page?.entities ?? []), ...(view?.entities ?? [])]) {
        const key = entityKey(entity);
        if (!seen.has(key)) {
            seen.add(key);
            entities.push(entity);
        }
    }

    const filters = {...(page?.filters ?? {}), ...(view?.filters ?? {})};
    const lens = view?.lens ?? page?.lens;

    return {
        path: route?.path ?? page?.path ?? '',
        title: page?.title ?? view?.title ?? route?.title ?? route?.path ?? '',
        url: route?.url ?? page?.url,
        entities,
        period: view?.period ?? page?.period,
        lens: lens ?? undefined,
        filters,
        selection: selection?.selection ?? [],
        capturedAt: capturedAt.toISOString()
    };
}
